import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { useDisconnectChannel } from '@/components/channels/queries';
import type { Channel } from '@/types/channel';
import { channelDisplayName, channelTypeNoun } from '@/lib/channel-display';
import { emit } from '@/lib/analytics';

interface DisconnectCloudDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channel: Channel;
  onDisconnected: () => void;
}

export function DisconnectCloudDialog({
  open,
  onOpenChange,
  channel,
  onDisconnected,
}: DisconnectCloudDialogProps) {
  const disconnect = useDisconnectChannel();
  const disconnecting = disconnect.isPending;
  const [confirmText, setConfirmText] = useState('');

  const displayName = channelDisplayName(channel);
  const typeNoun = channelTypeNoun(channel);
  // Confirmation phrase is always the literal word 'disconnect' — easy to
  // type on mobile, language-neutral, doesn't require the user to remember
  // an account handle that might be long or use special characters.
  const confirmed = confirmText === 'disconnect';

  function handleDisconnect() {
    if (!confirmed) return;
    disconnect.mutate(
      { channelId: channel.id },
      {
        onSuccess: () => {
          emit('channel_disconnected', {
            channel_id: channel.id,
            channel_type: channel.type,
            channel_display_name: displayName,
            connection_type: channel.connectionType,
          });
          toast.success('Channel disconnected');
          setConfirmText('');
          onOpenChange(false);
          onDisconnected();
        },
        onError: () =>
          toast.error(
            'Could not disconnect channel. Check your connection and try again.',
          ),
      },
    );
  }

  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!disconnecting) {
          onOpenChange(nextOpen);
          if (!nextOpen) setConfirmText('');
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Disconnect {displayName}?</AlertDialogTitle>
          <AlertDialogDescription>
            This disconnects your {typeNoun} and stops inbound delivery. Your
            channel ID and access token are kept, so you can reconnect it later
            without re-onboarding.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="grid gap-2 px-0">
          <label className="text-sm font-medium">
            Type &ldquo;disconnect&rdquo; to confirm:
          </label>
          <Input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            aria-label="Type disconnect to confirm disconnection"
            autoComplete="off"
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Keep Channel</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={!confirmed || disconnecting}
            onClick={handleDisconnect}
          >
            {disconnecting && (
              <Loader2 className="size-3.5 animate-spin" />
            )}
            {disconnecting ? 'Disconnecting...' : 'Disconnect Channel'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
